import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MapPin,
  ArrowRight,
  Loader2,
  X,
  Building2,
  Layers,
  Star,
  Flame,
  ListFilter,
  Building,
} from "lucide-react";
import Header from "@/components/Header";
import MapView, { HEAT_COLORS, buildHeatThresholds, formatHeatValue } from "@/components/MapView";
import api from "@/api/client";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const HEATMAP_METRICS = [
  { value: "permits_per_1k", label: "Permits per 1k residents", short: "Permits / 1k" },
  { value: "value_per_capita", label: "Construction $ per capita", short: "Value / capita" },
  { value: "multi_family_share", label: "Multi-family share (%)", short: "Multi-fam %" },
  { value: "yoy_change_pct", label: "YoY change 2022→2023 (%)", short: "YoY %" },
];

const PLACE_TYPE_COLORS = {
  city: "bg-blue-50 text-blue-700 border-blue-200",
  town: "bg-emerald-50 text-emerald-700 border-emerald-200",
  village: "bg-violet-50 text-violet-700 border-violet-200",
  township: "bg-amber-50 text-amber-700 border-amber-200",
  borough: "bg-rose-50 text-rose-700 border-rose-200",
  CDP: "bg-slate-100 text-slate-700 border-slate-200",
  municipality: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function MapWorkspace() {
  const navigate = useNavigate();
  const [geojson, setGeojson] = useState(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [placeResults, setPlaceResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFips, setSelectedFips] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatmapMetric, setHeatmapMetric] = useState("permits_per_1k");
  const [heatmap, setHeatmap] = useState(null);
  const [ahjIndex, setAhjIndex] = useState({ fips: [], geoids: [] });
  const [tab, setTab] = useState("counties");

  const { favorites, isFavorite, toggle } = useFavorites();

  const heatThresholds = useMemo(
    () => buildHeatThresholds(heatmap?.stats, heatmapMetric),
    [heatmap, heatmapMetric]
  );

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data.states || []));
    setGeoLoading(true);
    api
      .get("/counties/geojson")
      .then((r) => setGeojson(r.data))
      .catch(() => toast.error("Failed to load counties map"))
      .finally(() => setGeoLoading(false));
    api.get("/ahj/index").then((r) => setAhjIndex(r.data || { fips: [], geoids: [] }));
  }, []);

  // load heatmap whenever metric changes
  useEffect(() => {
    api.get(`/counties/heatmap?metric=${heatmapMetric}`).then((r) => setHeatmap(r.data));
  }, [heatmapMetric]);

  // debounced search
  useEffect(() => {
    const handle = setTimeout(async () => {
      const q = query.trim();
      const stateAbbr = stateFilter === "ALL" ? "" : stateFilter;
      if (!q && !stateAbbr) {
        setResults([]);
        setPlaceResults([]);
        return;
      }
      setSearching(true);
      try {
        if (tab === "counties") {
          const { data } = await api.get("/counties/search", { params: { q, state: stateAbbr, limit: 25 } });
          setResults(data.results || []);
        } else {
          const { data } = await api.get("/places/search", { params: { q, state: stateAbbr, limit: 30 } });
          setPlaceResults(data.results || []);
        }
      } catch {
        if (tab === "counties") setResults([]); else setPlaceResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, stateFilter, tab]);

  const stateFipsFilter = useMemo(() => {
    if (stateFilter === "ALL") return null;
    const found = states.find((s) => s.abbr === stateFilter);
    return found ? found.fips : null;
  }, [stateFilter, states]);

  const handleMapClick = async (fips) => {
    setSelectedPlace(null);
    setSelectedFips(fips);
    try {
      const { data } = await api.get(`/counties/${fips}`);
      setSelectedCounty(data);
    } catch {
      toast.error("Could not load county");
    }
  };

  const handleCountyResultClick = (county) => {
    setSelectedPlace(null);
    setSelectedFips(county.fips);
    setSelectedCounty(county);
  };

  const handlePlaceResultClick = async (place) => {
    setSelectedPlace(place);
    setSelectedFips(place.county_fips || null);
    if (place.county_fips) {
      try {
        const { data } = await api.get(`/counties/${place.county_fips}`);
        setSelectedCounty(data);
      } catch {
        setSelectedCounty(null);
      }
    }
  };

  const clearSelection = () => {
    setSelectedFips(null);
    setSelectedCounty(null);
    setSelectedPlace(null);
  };

  const handleToggleFav = async () => {
    if (!selectedCounty) return;
    try {
      const added = await toggle(selectedCounty);
      toast.success(added ? "Added to favorites" : "Removed from favorites");
    } catch {
      toast.error("Could not update favorites");
    }
  };

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-[#F8FAFC]">
      <Header />
      <div className="flex flex-1 h-[calc(100vh-4rem)] relative">
        {/* SIDEBAR */}
        <aside
          data-testid="map-sidebar"
          className="w-full md:w-[420px] h-full flex-none overflow-hidden bg-white border-r border-slate-200 shadow-sm z-40 relative flex flex-col"
        >
          {selectedCounty || selectedPlace ? (
            <ScrollArea className="flex-1">
              {selectedPlace ? (
                <SelectedPlacePanel
                  place={selectedPlace}
                  parentCounty={selectedCounty}
                  onClear={clearSelection}
                  onOpenPlace={() => navigate(`/place/${selectedPlace.geoid}`)}
                  onOpenCounty={() => selectedCounty && navigate(`/county/${selectedCounty.fips}`)}
                />
              ) : (
                <SelectedCountyPanel
                  county={selectedCounty}
                  isFav={isFavorite(selectedCounty.fips)}
                  onToggleFav={handleToggleFav}
                  onViewPermits={() => navigate(`/county/${selectedCounty.fips}`)}
                  onClear={clearSelection}
                />
              )}
            </ScrollArea>
          ) : (
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setQuery(""); }} className="flex-1 flex flex-col overflow-hidden">
              <div className="p-5 pb-3 flex-none border-b border-slate-200">
                <TabsList className="grid w-full grid-cols-3" data-testid="sidebar-tabs">
                  <TabsTrigger value="counties" data-testid="tab-counties"><Layers className="w-3.5 h-3.5 mr-1" />Counties</TabsTrigger>
                  <TabsTrigger value="places" data-testid="tab-places"><Building className="w-3.5 h-3.5 mr-1" />Places</TabsTrigger>
                  <TabsTrigger value="favorites" data-testid="tab-favorites">
                    <Star className="w-3.5 h-3.5 mr-1" />
                    Saved {favorites.length > 0 && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{favorites.length}</span>}
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* COUNTIES TAB */}
              <TabsContent value="counties" className="flex-1 overflow-hidden mt-0 flex flex-col">
                <div className="p-5 border-b border-slate-200 space-y-3 flex-none">
                  <SearchBar
                    label="Find a county"
                    placeholder="County, state, ZIP, FIPS…"
                    query={query}
                    setQuery={setQuery}
                    testId="county-search-input"
                  />
                  <StateSelect value={stateFilter} onChange={setStateFilter} states={states} />
                </div>
                <ScrollArea className="flex-1">
                  <CountyResults results={results} query={query} stateFilter={stateFilter} searching={searching} onPick={handleCountyResultClick} />
                </ScrollArea>
              </TabsContent>

              {/* PLACES TAB */}
              <TabsContent value="places" className="flex-1 overflow-hidden mt-0 flex flex-col">
                <div className="p-5 border-b border-slate-200 space-y-3 flex-none">
                  <SearchBar
                    label="Find a city / town / township"
                    placeholder="Charlotte, Brooklyn, Apex town…"
                    query={query}
                    setQuery={setQuery}
                    testId="place-search-input"
                  />
                  <StateSelect value={stateFilter} onChange={setStateFilter} states={states} />
                  <div className="text-[11px] text-slate-500 leading-relaxed">
                    Searches 67,000+ U.S. cities, towns, townships, boroughs, villages & CDPs.
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <PlaceResults results={placeResults} query={query} stateFilter={stateFilter} searching={searching} onPick={handlePlaceResultClick} />
                </ScrollArea>
              </TabsContent>

              {/* FAVORITES */}
              <TabsContent value="favorites" className="flex-1 overflow-hidden mt-0 flex flex-col">
                <ScrollArea className="flex-1">
                  <FavoritesList favorites={favorites} onPick={handleCountyResultClick} />
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </aside>

        {/* MAP */}
        <main className="flex-1 h-full z-0 relative" data-testid="map-container">
          {geoLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-100/70 backdrop-blur-sm">
              <div className="flex items-center gap-3 text-slate-700 bg-white rounded-md px-4 py-3 shadow-md border border-slate-200">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Loading 3,221 counties…</span>
              </div>
            </div>
          )}
          {geojson && (
            <MapView
              geojson={geojson}
              selectedFips={selectedFips}
              onSelect={handleMapClick}
              stateFipsFilter={stateFipsFilter}
              heatmapData={heatmap?.data}
              heatmapOn={heatmapOn}
              heatmapMetric={heatmapMetric}
              heatThresholds={heatThresholds}
              selectedPlace={selectedPlace}
              ahjFipsSet={new Set(ahjIndex.fips || [])}
            />
          )}

          {/* Heatmap control */}
          <div className="absolute top-4 right-4 z-[400] bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-md p-4 w-[290px]" data-testid="heatmap-control">
            <div className="flex items-center gap-3 mb-2">
              <Flame className="w-4 h-4 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">Permit heatmap</div>
                <div className="text-[10px] text-slate-500">Choropleth overlay</div>
              </div>
              <Switch checked={heatmapOn} onCheckedChange={setHeatmapOn} data-testid="heatmap-toggle" />
            </div>
            {heatmapOn && (
              <>
                <Select value={heatmapMetric} onValueChange={setHeatmapMetric}>
                  <SelectTrigger className="h-9 text-xs" data-testid="heatmap-metric-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HEATMAP_METRICS.map((m) => (
                      <SelectItem key={m.value} value={m.value} data-testid={`heatmap-metric-${m.value}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid="heatmap-legend">
                  <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                    {heatmap?.label || ""}
                  </div>
                  <div className="flex items-center gap-[1px]">
                    {heatThresholds.map((threshold, i) => {
                      const color = HEAT_COLORS[Math.min(i + 1, HEAT_COLORS.length - 1)];
                      return (
                        <div key={i} className="flex flex-col items-center flex-1">
                          <div className="w-full h-3" style={{ background: color.fillColor, borderTop: `1px solid ${color.color}` }} />
                          <div className="text-[9px] font-mono text-slate-500 mt-1">
                            {formatHeatValue(threshold, heatmapMetric)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {!heatmapOn && (
            <div className="absolute bottom-4 left-4 z-[400] bg-white/95 backdrop-blur border border-slate-200 rounded-md shadow-md px-3 py-2.5 text-xs">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1.5">Legend</div>
              <div className="flex items-center gap-2 mb-1"><span className="inline-block w-3 h-3 rounded-sm bg-slate-300 border border-slate-400" /> County</div>
              <div className="flex items-center gap-2 mb-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500 border border-emerald-700" /> Selected county</div>
              <div className="flex items-center gap-2 mb-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-500 border border-blue-700" /> Selected place</div>
              <div className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-full bg-indigo-500 ring-2 ring-white" /> AHJ field intel ({ahjIndex.fips?.length || 0})</div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function SearchBar({ label, placeholder, query, setQuery, testId }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid={testId}
          placeholder={placeholder}
          className="pl-9 h-10"
        />
        {query && (
          <button onClick={() => setQuery("")} data-testid="search-clear-btn" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {/^\d{5}$/.test(query.trim()) && (
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-700 mt-1.5">ZIP lookup</div>
      )}
    </div>
  );
}

function StateSelect({ value, onChange, states }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">Filter state</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="state-filter-select" className="h-10">
          <SelectValue placeholder="All states" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="ALL" data-testid="state-option-all">All states</SelectItem>
          {states.map((s) => (
            <SelectItem key={s.fips} value={s.abbr} data-testid={`state-option-${s.abbr}`}>
              {s.name} ({s.abbr})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SelectedCountyPanel({ county, isFav, onToggleFav, onViewPermits, onClear }) {
  const permits = county.permits_2023 || { total_bldgs: 0, total_units: 0 };
  return (
    <div className="p-5 space-y-5" data-testid="selected-county-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-700 mb-2 flex items-center gap-1.5">
            <MapPin className="w-3 h-3" /> County selected
          </div>
          <h2 className="font-outfit text-2xl font-bold text-slate-900 leading-tight" data-testid="selected-county-name">
            {county.full_name}
          </h2>
          <div className="text-sm text-slate-500 mt-1">{county.state_name}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggleFav} data-testid="favorite-toggle-btn"
            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
            className={`p-2 rounded-md transition-colors ${isFav ? "text-amber-500 bg-amber-50 hover:bg-amber-100" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}`}>
            <Star className="w-5 h-5" fill={isFav ? "currentColor" : "none"} />
          </button>
          <button onClick={onClear} data-testid="clear-selection-btn" className="p-2 text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <DataTile label="FIPS code" value={county.fips} mono />
        <DataTile label="State" value={county.state_abbr} />
        <DataTile label="Population" value={(county.population || 0).toLocaleString()} />
        <DataTile label="Area (sq mi)" value={Math.round(county.land_area_sq_mi || 0).toLocaleString()} />
      </div>

      <div className="border border-slate-200 rounded-md p-4 bg-amber-50/40">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-amber-700 mb-2">
          <Flame className="w-3 h-3" /> 2023 building permits (Census BPS)
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Permits issued</div>
            <div className="font-outfit text-xl font-bold text-slate-900">{permits.total_bldgs.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Per 1k residents</div>
            <div className="font-outfit text-xl font-bold text-slate-900">{(county.permits_per_1k || 0).toFixed(2)}</div>
          </div>
        </div>
      </div>

      <Button onClick={onViewPermits} data-testid="view-permits-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
        <Building2 className="w-4 h-4 mr-2" /> View permits
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

function SelectedPlacePanel({ place, parentCounty, onClear, onOpenPlace, onOpenCounty }) {
  const typeBadge = PLACE_TYPE_COLORS[place.type] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="p-5 space-y-5" data-testid="selected-place-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700 mb-2 flex items-center gap-1.5">
            <Building className="w-3 h-3" /> Place selected
          </div>
          <h2 className="font-outfit text-2xl font-bold text-slate-900 leading-tight" data-testid="selected-place-name">
            {place.full_name}
          </h2>
          <div className="text-sm text-slate-500 mt-1">{place.state_name}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge className={`${typeBadge} font-mono capitalize`}>{place.type}</Badge>
            {place.lsad_label && <Badge variant="outline" className="font-mono">{place.lsad_label}</Badge>}
          </div>
        </div>
        <button onClick={onClear} data-testid="clear-selection-btn" className="p-2 text-slate-400 hover:text-slate-700 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <DataTile label="Land area (sq mi)" value={place.area_sq_mi.toFixed(2)} />
        <DataTile label="State" value={place.state_abbr} mono />
      </div>

      {parentCounty && (
        <div className="border border-emerald-200 rounded-md p-4 bg-emerald-50/40" data-testid="place-parent-county">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-700 mb-2 flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> Parent county
          </div>
          <div className="font-outfit text-lg font-semibold text-slate-900">{parentCounty.full_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">FIPS {parentCounty.fips} · pop {(parentCounty.population||0).toLocaleString()}</div>
        </div>
      )}

      <div className="space-y-2">
        <Button onClick={onOpenPlace} data-testid="view-place-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
          <Building className="w-4 h-4 mr-2" /> Open {place.name}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        {parentCounty && (
          <Button onClick={onOpenCounty} data-testid="view-place-county-btn" variant="outline" className="w-full h-11">
            <Building2 className="w-4 h-4 mr-2" /> Open {parentCounty.full_name}
          </Button>
        )}
      </div>
    </div>
  );
}

function DataTile({ label, value, mono }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">{label}</div>
      <div className={`text-base font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function CountyResults({ results, query, stateFilter, searching, onPick }) {
  if (searching) return <LoadingRow />;
  if (!query && stateFilter === "ALL") return <CountiesEmptyState />;
  if (results.length === 0) return <div className="p-5 text-sm text-slate-500" data-testid="no-results">No counties matched.</div>;
  return (
    <div className="divide-y divide-slate-100" data-testid="search-results-list">
      {results.map((c) => (
        <button key={c.fips} onClick={() => onPick(c)} data-testid={`result-row-${c.fips}`}
          className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group">
          <div>
            <div className="text-sm font-medium text-slate-900">{c.full_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.state_name} · FIPS {c.fips}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
        </button>
      ))}
    </div>
  );
}

function PlaceResults({ results, query, stateFilter, searching, onPick }) {
  if (searching) return <LoadingRow />;
  if (!query && stateFilter === "ALL") {
    return (
      <div className="p-5" data-testid="places-empty">
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
            <Building className="w-3 h-3" /> Cities, towns, townships
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Type any city, town, township, village, borough or CDP — e.g. <span className="font-mono">Charlotte</span> or <span className="font-mono">Brooklyn</span> — to see its parent county and permitting info.
          </p>
        </div>
      </div>
    );
  }
  if (results.length === 0) return <div className="p-5 text-sm text-slate-500" data-testid="no-place-results">No places matched.</div>;
  return (
    <div className="divide-y divide-slate-100" data-testid="place-results-list">
      {results.map((p) => {
        const badge = PLACE_TYPE_COLORS[p.type] || "bg-slate-100 text-slate-700 border-slate-200";
        return (
          <button key={p.geoid} onClick={() => onPick(p)} data-testid={`place-row-${p.geoid}`}
            className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{p.full_name}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">
                {p.state_name} {p.county_name ? `· ${p.county_name}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-none">
              <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border capitalize ${badge}`}>{p.type}</span>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FavoritesList({ favorites, onPick }) {
  if (favorites.length === 0) {
    return (
      <div className="p-5" data-testid="favorites-empty">
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-5 text-center">
          <Star className="w-6 h-6 mx-auto text-slate-300 mb-2" />
          <div className="text-sm font-medium text-slate-700 mb-1">No favorites yet</div>
          <p className="text-xs text-slate-500 leading-relaxed">Click the star icon when a county is selected to save it here.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-100" data-testid="favorites-list">
      {favorites.map((f) => (
        <button key={f.fips} onClick={() => f.county && onPick(f.county)} data-testid={`favorite-row-${f.fips}`}
          className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between group">
          <div>
            <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-amber-400 text-amber-500" />
              {f.county ? f.county.full_name : `FIPS ${f.fips}`}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{f.county ? `${f.county.state_name} · FIPS ${f.fips}` : "Unknown county"}</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 transition-colors" />
        </button>
      ))}
    </div>
  );
}

function CountiesEmptyState() {
  return (
    <div className="p-5 space-y-4" data-testid="sidebar-empty-state">
      <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
          <Layers className="w-3 h-3" /> Tip
        </div>
        <p className="text-sm text-slate-700 leading-relaxed">
          Click a county on the map, search by name / FIPS / ZIP, or filter by state. Switch to <strong>Places</strong> for cities & townships.
        </p>
      </div>
      <div className="space-y-2">
        {[
          { v: "3,221", l: "counties" },
          { v: "67k+", l: "places" },
          { v: "33,791", l: "ZIP codes" },
          { v: "2014–2023", l: "permit history" },
        ].map((s) => (
          <div key={s.l} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
            <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{s.l}</span>
            <span className="text-base font-medium text-slate-900">{s.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingRow() {
  return <div className="p-5 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>;
}
